import React, { useState, useEffect, lazy, Suspense, useCallback } from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate } from 'react-router-dom';
import { ThemeProvider } from '@mui/material/styles';
import CssBaseline from '@mui/material/CssBaseline';
import { QueryClientProvider } from '@tanstack/react-query';
import HomePage from './components/HomePage';
import Login from './components/Login';
import ClientLogin from './components/ClientLogin';
import LazyFallback from './components/LazyFallback';
import { AuthContext } from './context/AuthContext';
import { queryClient } from './query/queryClient';
import createAppTheme from './theme/createAppTheme';

// Role dashboards — code-split; only the logged-in role's chunk downloads
const TechnicianDashboard = lazy(() => import('./components/TechnicianDashboard'));
const AdminDashboard = lazy(() => import('./components/AdminDashboard'));
const ManagementDashboard = lazy(() => import('./components/ManagementDashboard'));
const DeveloperDashboard = lazy(() => import('./components/DeveloperDashboard'));
const ClientDashboard = lazy(() => import('./components/ClientDashboard'));

const DASHBOARD_IMPORTS = {
  technician: () => import('./components/TechnicianDashboard'),
  admin: () => import('./components/AdminDashboard'),
  management: () => import('./components/ManagementDashboard'),
  developer: () => import('./components/DeveloperDashboard'),
  client: () => import('./components/ClientDashboard'),
};

const getTheme = (mode) => createAppTheme(mode);

function RequireRole({ user, role, children }) {
  if (!user) return <Navigate to="/login" replace />;
  if (user.role === role) return children;
  return <Navigate to={`/${user.role}`} replace />;
}

function RequireManagement({ user, children }) {
  if (!user) return <Navigate to="/login" replace />;
  if (user.role === 'management') return children;
  return <Navigate to={`/${user.role}`} replace />;
}

const SESSION_TTL_MS = 4 * 60 * 60 * 1000; // 4 hours (fallback if API omits expires_at)

function withSessionExpiry(userData) {
  if (!userData || typeof userData !== 'object') return userData;
  let expiresAtMs;
  if (userData.expires_at != null) {
    const n = Number(userData.expires_at);
    expiresAtMs = n < 1e12 ? n * 1000 : n; // seconds → ms
  } else if (userData.expires_in != null) {
    expiresAtMs = Date.now() + Number(userData.expires_in) * 1000;
  } else {
    expiresAtMs = Date.now() + SESSION_TTL_MS;
  }
  return {
    ...userData,
    expires_at: Math.floor(expiresAtMs / 1000),
    sessionExpiresAt: expiresAtMs,
  };
}

function isSessionExpired(userData) {
  if (!userData) return true;
  const ms = userData.sessionExpiresAt || (userData.expires_at ? Number(userData.expires_at) * 1000 : null);
  if (!ms) return false; // legacy sessions without expiry — let API enforce
  return Date.now() >= ms;
}

function App() {
  const [user, setUser] = useState(null);
  const [darkMode, setDarkMode] = useState(() => {
    const saved = localStorage.getItem('darkMode');
    return saved ? JSON.parse(saved) : false;
  });

  const logout = useCallback(() => {
    setUser(null);
    localStorage.removeItem('user');
  }, []);

  useEffect(() => {
    const storedUser = localStorage.getItem('user');
    if (storedUser) {
      try {
        const parsed = JSON.parse(storedUser);
        if (parsed?.token && !isSessionExpired(parsed)) {
          setUser(parsed);
        } else {
          localStorage.removeItem('user');
        }
      } catch {
        localStorage.removeItem('user');
      }
    }
  }, []);

  // Enforce 4h session client-side (check periodically + on tab focus)
  useEffect(() => {
    if (!user) return undefined;
    const check = () => {
      if (isSessionExpired(user)) {
        logout();
        window.location.assign('/login');
      }
    };
    check();
    const id = window.setInterval(check, 60_000);
    const onFocus = () => check();
    window.addEventListener('focus', onFocus);
    return () => {
      window.clearInterval(id);
      window.removeEventListener('focus', onFocus);
    };
  }, [user, logout]);

  useEffect(() => {
    localStorage.setItem('darkMode', JSON.stringify(darkMode));
    document.documentElement.setAttribute('data-theme', darkMode ? 'dark' : 'light');
  }, [darkMode]);

  const login = (userData) => {
    const withExpiry = withSessionExpiry(userData);
    setUser(withExpiry);
    localStorage.setItem('user', JSON.stringify(withExpiry));
    // Prefetch role dashboard chunk while navigating
    const prefetch = DASHBOARD_IMPORTS[withExpiry?.role];
    if (prefetch) prefetch().catch(() => {});
  };

  // Prefetch dashboard when restoring an existing session
  useEffect(() => {
    if (!user?.role) return undefined;
    const prefetch = DASHBOARD_IMPORTS[user.role];
    if (!prefetch) return undefined;
    const idle = window.requestIdleCallback
      ? window.requestIdleCallback(() => prefetch().catch(() => {}))
      : window.setTimeout(() => prefetch().catch(() => {}), 200);
    return () => {
      if (window.cancelIdleCallback && typeof idle === 'number') {
        try {
          window.cancelIdleCallback(idle);
        } catch {
          window.clearTimeout(idle);
        }
      } else {
        window.clearTimeout(idle);
      }
    };
  }, [user?.role]);

  const theme = getTheme(darkMode ? 'dark' : 'light');

  return (
    <QueryClientProvider client={queryClient}>
      <ThemeProvider theme={theme}>
        <CssBaseline />
        <AuthContext.Provider value={{ user, login, logout, darkMode, setDarkMode }}>
          <Router>
            <Suspense fallback={<LazyFallback fullPage label="Loading workspace…" />}>
              <Routes>
                <Route path="/" element={<HomePage />} />
                <Route
                  path="/login"
                  element={user ? <Navigate to={`/${user.role}`} replace /> : <Login />}
                />
                <Route
                  path="/client-login"
                  element={user ? <Navigate to={`/${user.role}`} replace /> : <ClientLogin />}
                />
                <Route
                  path="/technician"
                  element={
                    <RequireRole user={user} role="technician">
                      <TechnicianDashboard />
                    </RequireRole>
                  }
                />
                <Route
                  path="/admin"
                  element={
                    <RequireRole user={user} role="admin">
                      <AdminDashboard />
                    </RequireRole>
                  }
                />
                <Route
                  path="/management"
                  element={
                    <RequireManagement user={user}>
                      <ManagementDashboard />
                    </RequireManagement>
                  }
                />
                <Route
                  path="/management/console"
                  element={
                    <RequireManagement user={user}>
                      <AdminDashboard />
                    </RequireManagement>
                  }
                />
                <Route
                  path="/developer"
                  element={
                    <RequireRole user={user} role="developer">
                      <DeveloperDashboard />
                    </RequireRole>
                  }
                />
                <Route
                  path="/client"
                  element={
                    <RequireRole user={user} role="client">
                      <ClientDashboard />
                    </RequireRole>
                  }
                />
              </Routes>
            </Suspense>
          </Router>
        </AuthContext.Provider>
      </ThemeProvider>
    </QueryClientProvider>
  );
}

export default App;

